import { Injectable, inject } from '@angular/core';
import { catchError, finalize, first, map, mergeMap, of, tap } from 'rxjs';

/** rose-portal-service ErrorConstants.ERR_USERNAME_ALREADY_EXISTS: POST /users for a phone that has an account. */
export const USERNAME_ALREADY_EXISTS_ERROR_CODE = '000670';
import { EffectsBase } from 'clicklease-ui-elements';
import { RoseLogged, TokenResponse } from '../../interfaces';
import {
    AuthService,
    OtpService,
    RoseService,
    TokensService,
    UserpilotService,
    UserService,
} from '../../services';
import {
    IsLoadedAuthDataAction,
    LoadAuthDataAction,
    LoadBankAccountListAction,
    OTPAction,
    ResetAuthDataAction,
    ResetBankAccountListAction,
    ResetBusinessInfoAction,
    ResetPersonalInfoAction,
    SetLoggedDataAction,
    SetBusinessInfoAction,
    SetPasswordDataAction,
    SetPersonalInfoAction,
    SetPhoneNumberDataAction,
    SetUserIdentityAction,
    ValidateLogedUserAction,
    loginParamsSelector,
    getPhoneNumberSelector,
    LoadContractsAction,
    ResetContractsAction,
    getRoseKeySelector,
    SetIsUserValidAction,
    SetAuthErrorAction,
    SetOTPDataAction,
    SetIsOTPValidAction,
    SetIsOtpInvalidAction,
    PutPasswordDataAction,
    getAccessToken,
    PutPasswordDataSuccessAction,
    PutPasswordDataErrorAction,
    PostUserAction,
    getUserSelectedSelector,
    PostUserSuccessAction,
    PostUserErrorAction,
    isCreatingUserSelector,
    SetLoginDataAction,
    getIsAdminSelector,
    LoadCustomerDataAction,
    RemoveCustomerDataAction,
    SetIsAdmin,
    ResetAdminAuthDataAction
} from '..';
import { HttpErrorResponse } from '@angular/common/http';
import { ofType } from '@ngrx/effects';

import { jwtDecode } from "jwt-decode";

@Injectable()
export class AuthDataEffect extends EffectsBase {

    /**
     * @private
     * @readonly
     * @property
     * @description This property contains the injection for the AuthService.
     */
    readonly #authService = inject(AuthService);

    /**
     * @private
     * @readonly
     * @property
     * @description This property contains the injection for the OtpService.
     */
    readonly #otpService = inject(OtpService);

    /**
     * @private
     * @readonly
     * @property
     * @description This property contains the injection for the UserpilotService, used to end
     * the analytics session when the ROSE logs out.
     */
    readonly #userpilot = inject(UserpilotService);

    /**
     * @private
     * @readonly
     * @property
     * @description This property contains the injection for the RoseService.
     */
    readonly #roseService = inject(RoseService);

    /**
     * @private
     * @readonly
     * @property
     * @description This property contains the injection for the TokensService.
     */
    readonly #tokensService = inject(TokensService);

    /**
     * @private
     * @readonly
     * @property
     * @description This property contains the injection for the UserService
     */
    readonly #userService = inject(UserService);

    /**
     * @private
     * @readonly
     * @property
     * @description This property contains the signal selector for the login
     * credentials params.
     */
    readonly #loginCredentials = this.store.selectSignal(loginParamsSelector);

    /**
     * @private
     * @readonly
     * @property
     * @description This property contains the signal selector for the current
     * rose key.
     */
    readonly #roseKey = this.store.selectSignal(getRoseKeySelector);

    /**
     * @private
     * @readonly
     * @property
     * @description This property contains the signal selector for the user
     * phone number.
     */
    readonly #userPhone = this.store.selectSignal(getPhoneNumberSelector);

    readonly #accessToken = this.store.selectSignal(getAccessToken);

    readonly #selectedUser = this.store.selectSignal(getUserSelectedSelector);

    readonly #isCreating = this.store.selectSignal(isCreatingUserSelector);

    readonly #isAdmin = this.store.selectSignal(getIsAdminSelector);

    public readonly doOTP = this.createEffect(() =>
        this.actions.pipe(
            this.ofType(OTPAction),
            mergeMap(() => this.OTPEffect())
        )
    );

    public readonly resetData = this.createEffect(() =>
        this.actions.pipe(
            this.ofType(ResetAuthDataAction),
            mergeMap(() => this.logoutEffect())
        )
    );

    public readonly resetAdminData = this.createEffect(() =>
        this.actions.pipe(
            this.ofType(ResetAdminAuthDataAction),
            mergeMap(() => this.logoutEffect())
        )
    );

    public readonly setLoggedData = this.createEffect(() =>
        this.actions.pipe(
            this.ofType(SetLoggedDataAction),
            mergeMap(() => this.tokensEffect())
        )
    );

    public readonly setPassword = this.createEffect(() =>
        this.actions.pipe(
            this.ofType(SetPasswordDataAction),
            mergeMap(() => this.loginEffect())
        )
    );

    public readonly adminLoginAction = this.createEffect(() =>
        this.actions.pipe(
            this.ofType(SetLoginDataAction),
            mergeMap(() => this.loginEffect())
        )
    );

    /**
     * @description Continue on the login page moves straight to the password
     * step. It no longer asks the server whether the phone has an account,
     * because that answer let anyone enumerate customers (SNP-241). An unknown
     * phone and a wrong password now fail the same way, at the token call.
     */
    public readonly setPhoneNumber = this.createEffect(() =>
        this.actions.pipe(
            this.ofType(SetPhoneNumberDataAction),
            map(() => SetIsUserValidAction({ valid: true }))
        )
    );

    public readonly setUserIdentity = this.createEffect(() =>
        this.actions.pipe(
            this.ofType(SetUserIdentityAction),
            mergeMap(() => this.loadUserIndentity())
        )
    );

    public readonly validateLoggedUser = this.createEffect(() =>
        this.actions.pipe(
            this.ofType(ValidateLogedUserAction),
            mergeMap(() => this.validateLoggedUserEffect())
        )
    );

    public readonly setCustomerData = this.createEffect(() =>
        this.actions.pipe(
            ofType(LoadCustomerDataAction),
            mergeMap(({ roseKey }) => this.loadUserAsAdmin(roseKey))
        )
    );

    public readonly resetCustomerData = this.createEffect(() =>
        this.actions.pipe(
            ofType(RemoveCustomerDataAction),
            map(() => this.resetRose())
        )
    );

    SetOTPDataActionEffect = this.createEffect(() =>
        this.actions.pipe(
            ofType(SetOTPDataAction),
            mergeMap(({ otpData }) =>
                this.#otpService.validateOTP(otpData).pipe(
                    map((data) =>
                        data.success
                        ? SetIsOTPValidAction({ otpData: data })
                        : SetIsOtpInvalidAction()
                    ),
                    catchError((error) => of(SetIsOtpInvalidAction())),
                    first()
                )
            )
        )
    );

    /**
     * @description Creates the account. Since SNP-241 the OTP comes first and this runs from the
     * DOB / last-4 SSN page ("Find my account"), only for a phone the OTP step reported as having
     * no account. The service checks the ROSE match and creates the user with a temporary password;
     * Set password then replaces it.
     */
    postUserEffect = this.createEffect(() =>
        this.actions.pipe(
            ofType(PostUserAction),
            mergeMap(() => {
                if (this.#isCreating()) {
                    return this.#userService.postUser(this.#accessToken(), {
                        dateOfBirth: this.#selectedUser().dbo,
                        last4ssn: this.#selectedUser().ssn,
                        phoneNumber: this.#userPhone()
                    }).pipe(
                        map((data) => PostUserSuccessAction()),
                        catchError((error: HttpErrorResponse) =>
                            of(PostUserErrorAction({ alreadyExists: this.isUserAlreadyExistsError(error) }))
                        ),
                        first()
                    )
                } else {
                    return of(PostUserSuccessAction());
                }
            }))
    );

    putPasswordDataEffect = this.createEffect(() =>
        this.actions.pipe(
            ofType(PutPasswordDataAction),
            mergeMap(({ passwordData }) => {
                return this.#userService.putPassword(this.#accessToken(), passwordData, this.#userPhone()).pipe(
                    map((data) => PutPasswordDataSuccessAction()),
                    catchError((error) => of(PutPasswordDataErrorAction())),
                    first()
                )
            }))
    );

    private loginEffect() {
        const isAdmin = this.#isAdmin();
        return this.#authService.login(this.#loginCredentials()).pipe(
            map(
                (response: TokenResponse) => {
                    if(isAdmin) {
                        if(!this.tokenHasAdminUserRole(response.access_token)) {
                            throw new HttpErrorResponse({ error: { 
                                error: 'invalid_grant',
                                error_description: 'The user is not authorized to access this resource' 
                            }});
                        }
                    }
                    return this.setTokenData(response);
                }
            ),
            catchError(({ error }: HttpErrorResponse) =>
                of(SetAuthErrorAction(error))
            )
        );
    }

    private tokenHasAdminUserRole(token: string): boolean {
        const decodedToken: { user_roles: string[] } = jwtDecode(token);
        const roles = decodedToken?.user_roles || [];
        return roles.includes('Clicklease Admin');
    }

    private logoutEffect() {
        const loginUrl = this.#isAdmin() ? '/admin/login' : '/login';
        return this.#authService.logout(this.#tokensService.refreshToken).pipe(
            map(() => this.resetAuthAndRoseData()),
            catchError(() => of(this.resetAuthAndRoseData())),
            finalize(() => {
                // In `finalize` so it runs whether or not the logout request itself succeeded.
                this.#userpilot.endSession();

                // A full document load, deliberately, rather than a router navigation.
                //
                // UserPilot's session and its socket live as long as the page does, and the npm
                // wrapper loads the SDK exactly once — nothing can restart it in place. Replacing
                // the document is therefore the only way to give the next ROSE to sign in a fresh
                // session and a fresh connection instead of inheriting this one.
                //
                // It also guarantees no root-provided singleton carries her data into the next
                // session, which a router navigation cannot promise.
                window.location.assign(loginUrl);
            })
        );
    }

    private resetAuthAndRoseData() {
        this.store.dispatch(SetIsAdmin({isAdmin: false}));
        return this.resetRose();
    }

    private loadUserAsAdmin(roseKey: string) {
        return this.#roseService.getRoseData(roseKey).pipe(
            map((response: RoseLogged) => this.setRose(response)),
            catchError(() => {
                return of(ResetAuthDataAction());
            })
        );
    }

    private loadUserIndentity() {
        return this.#roseService.getRoseData(this.#roseKey()).pipe(
            map((response: RoseLogged) => this.setRose(response)),
            catchError(() => {
                this.#tokensService.clearTokens();
                return of(ResetAuthDataAction());
            })
        );
    }

    private OTPEffect() {
        return this.#otpService
            .sendOtp(this.#userPhone())
            .pipe(map(() => LoadAuthDataAction()));
    }

    private resetRose() {
        if(!this.#isAdmin())
            this.#tokensService.clearTokens();
        this.store.dispatch(ResetBankAccountListAction());
        this.store.dispatch(ResetBusinessInfoAction());
        this.store.dispatch(ResetContractsAction());
        return ResetPersonalInfoAction();
    }

    private setRose({ businessInformation, personalInformation }: RoseLogged) {
        this.store.dispatch(SetPersonalInfoAction(personalInformation));
        this.store.dispatch(SetBusinessInfoAction(businessInformation));
        this.store.dispatch(LoadBankAccountListAction());
        this.store.dispatch(LoadContractsAction());
        return IsLoadedAuthDataAction();
    }

    private setTokenData({
        access_token,
        expires_in,
        refresh_token,
        refresh_expires_in,
    }: TokenResponse) {
        this.#tokensService.setTokens(access_token, refresh_token);
        this.#tokensService.setExpiressIn(
            refresh_expires_in,
            expires_in
        );

        return SetLoggedDataAction();
    }

    private tokensEffect() {
        return this.#isAdmin() ? 
            of(IsLoadedAuthDataAction()) :
            this.#authService.getAuthData().pipe(
                tap((response) => {
                    const userPhone = this.#userPhone();
                    if(userPhone !== null && userPhone !== '')
                        this.#roseService.setLastLogin(response.rosekey).pipe(
                            catchError((error) => {
                                console.error('Failed to update last login:', error);
                                return of(null);
                            })
                        ).subscribe();
                }),
                map((response) => SetUserIdentityAction({ userInfoData: response })),
                catchError(() => of(ResetAuthDataAction()))
        );
    }

    private validateLoggedUserEffect() {
        this.store.dispatch(
            SetIsAdmin({
                isAdmin: this.#tokensService.hasAccessTokenStored && this.tokenHasAdminUserRole(this.#tokensService.accessToken)
            })
        );
        if (this.#tokensService.hasAccessTokenStored) {
            return of(SetLoggedDataAction());
        } else return of(LoadAuthDataAction());
    }

    /**
     * @private
     * @description The service answers error code 000670 when the phone already has an
     * account. Only someone who has just passed the SMS code gets this far, so telling
     * them is safe. Matched on the code, never on the message text.
     */
    private isUserAlreadyExistsError(error: HttpErrorResponse): boolean {
        return error?.error?.errorCode === USERNAME_ALREADY_EXISTS_ERROR_CODE;
    }
}
